"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";

import LoginPopup from "@/components/Shortlist/LoginPopup";
import { CommercetoolsProject } from "@/types";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";
import Modal from "../ui/Modal/Modal";
import ModalContent from "../ui/Modal/ModalContent";
import { handleShortlist, handleShortlistAdd, handleShortlistRemove, handleShortlistRemoveAll } from "./actions";
import { NotificationType } from "./type";

interface NotificationContextType {
  favorites: CommercetoolsProject[];
  showNotification: (message: string, duration: number, typeMessage: NotificationType) => void;
  hideNotification: () => void;
  addFavorites: (item: CommercetoolsProject, duration: number, typeMessage: NotificationType) => Promise<boolean>;
  isFavorite: (itemId: string) => boolean;
  deleteFavorites: (items: CommercetoolsProject[]) => Promise<boolean>;
  deleteAllFavorites: () => Promise<boolean>;
  askLogin: () => void;
  setFavorites: (units: CommercetoolsProject[]) => void;
  softLoginModalOpen: boolean;
  setSoftLoginModalOpen: (open: boolean) => void;
  showUnavailableUnit: (backTo?: string) => void;
  inventoryIDs: Record<string, string[]>;
  loadedFavourites: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification deve essere utilizzato all'interno di un NotificationProvider");
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const t = useTranslations();
  const router = useRouter();
  const path = usePathname();
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState("");
  const [typeMessage, setTypeMessage] = useState("");
  const [notificationTimer, setNotificationTimer] = useState<NodeJS.Timeout | null>(null);
  const [softLoginModalOpen, setSoftLoginModalOpen] = useState<boolean>(false);

  // state specific for the unavailable property popup
  const [showUnavailableUnitPopup, setShowUnavailableUnitPopup] = useState(false);
  // this define the behavior on close, if it should redirects the user back to a specific url or just close
  const [redirectOnClose, setRedirectOnClose] = useState<string>();

  const showUnavailableUnit = (backTo?: string) => {
    setShowUnavailableUnitPopup(true);
    if (backTo) {
      setRedirectOnClose(backTo);
    }
  };

  const handleCloseUnavailablePopup = () => {
    setShowUnavailableUnitPopup(false);
    if (redirectOnClose) {
      // in case of checkout just go back
      if (redirectOnClose === "checkout") {
        router.back();
      } else {
        router.push(redirectOnClose, { scroll: true });
      }
      setRedirectOnClose(undefined);
    }
  };

  const [favorites, setFavorites] = useState<CommercetoolsProject[]>([]);
  const [currentFavoriteId, setCurentFavoriteId] = useState(favorites?.length > 0 ? favorites[favorites?.length - 1]?.id : undefined);
  const [loadedFavourites, setLoadedFavourites] = useState(false);

  const showNotification = (message: string, duration = 3000, typeMessage: string) => {
    setMessage(message);
    setIsVisible(true);
    setTypeMessage(typeMessage);

    if (notificationTimer) {
      clearTimeout(notificationTimer);
    }

    const timer = setTimeout(() => {
      setIsVisible(false);
    }, duration);

    setNotificationTimer(timer);
  };

  const hideNotification = () => {
    setIsVisible(false);
  };

  const addFavorites = async (item: CommercetoolsProject, duration = 5000, typeMessage: NotificationType) => {
    const response = await handleShortlistAdd(item?.id);
    if (response.error) {
      return false;
    }
    setFavorites(response?.units);
    setCurentFavoriteId(item?.id);
    // Bug 79818
    setTypeMessage(typeMessage);
    setIsVisible(true);
    if (notificationTimer) {
      clearTimeout(notificationTimer);
    }
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, duration);
    setNotificationTimer(timer);

    return true;
  };

  const deleteFavorites = async (items: CommercetoolsProject[]) => {
    const response = await handleShortlistRemove(items?.map((x) => x?.id));
    if (response.error) {
      return false;
    }
    setFavorites(response?.units);
    return true;
  };

  const deleteAllFavorites = async () => {
    const response = await handleShortlistRemoveAll();
    if (response.error) {
      return false;
    }
    setFavorites([]);
    return true;
  };

  const isFavorite = (itemId: string): boolean => {
    return favorites?.some((favorite) => favorite?.id === itemId);
  };

  const askLogin = () => {
    setIsVisible(true);
    setTypeMessage(NotificationType.LOGIN);
  };

  // Effect responsible for initial loading of the shortlist
  // useEffect(() => {
  //   // console.info("session or path upodated ", session, path);
  //   handleShortlist().then((x) => {
  //     if (!x.error) {
  //       setFavorites(x?.units);
  //     }
  //     setLoadedFavourites(true);
  //   });
  // }, [path]);

  return (
    <NotificationContext.Provider
      value={{
        favorites,
        showNotification,
        hideNotification,
        addFavorites,
        deleteFavorites,
        deleteAllFavorites,
        isFavorite,
        askLogin,
        setFavorites,
        softLoginModalOpen,
        setSoftLoginModalOpen,
        showUnavailableUnit,
        inventoryIDs: {},
        loadedFavourites,
      }}
    >
      <AnimatePresence>
        {isVisible && typeMessage !== NotificationType.ADD && typeMessage !== NotificationType.LOGIN && (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.2 }}
            className={`w-full font-secondary font-normal text-s sticky top-0 z-[60] ${typeMessage == NotificationType.ERROR ? "text-[#B42318] bg-[#F7E9E8]" : "bg-[#E6F1ED] text-[#067647]"} py-m text-center `}
          >
            {message}
          </motion.div>
        )}
      </AnimatePresence>

      {isVisible && typeMessage == NotificationType.LOGIN && <LoginPopup closeFunc={setIsVisible} />}
      {/* unavailable popup */}
      <Modal open={showUnavailableUnitPopup} onClose={handleCloseUnavailablePopup}>
        <ModalContent className="w-full md:w-12\/24 mx-auto p-l">
          <Button
            className="absolute z-50 text-xl cursor-pointer text-black stroke-black-10 right-l top-l rtl:left-l rtl:right-auto hover:text-black"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.TERTIARY}
            theme={ButtonTheme.BLACK}
            onClick={handleCloseUnavailablePopup}
          >
            <span className="pointer-events-none">
              <Icon name="close" size="24" title="close" />
              <span className="sr-only">{t("button.label.close")}</span>
            </span>
          </Button>
          <div className="flex items-center justify-between mb-m">
            <h3 className="font-primary font-medium uppercase text-m">{t("checkout.unavailableProduct")}</h3>
          </div>
          <div aria-live="polite" className={`w-full font-secondary font-normal text-s text-[#B42318] bg-[#F7E9E8] p-m text-center`}>
            {t("checkout.unavailableProductDesc")}
          </div>
          <div className="flex justify-end mt-l">
            <Button
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.PRIMARY}
              theme={ButtonTheme.BLACK}
              onClick={handleCloseUnavailablePopup}
              customClass={`flex items-center gap-xxs border-none font-medium`}
            >
              {t("checkout.unavailableProductBack")}
            </Button>
          </div>
        </ModalContent>
      </Modal>
      {children}
    </NotificationContext.Provider>
  );
};
